import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@noor/api';
import { confirmDialog, toast } from '@noor/lib';
import type { User } from '@/entities/user';
import { useDeleteUser } from './use-delete-user';

/**
 * The «Удалить сотрудника» action, shared by the desktop table row and the
 * mobile edit sheet so the two cannot drift on what the button actually does.
 *
 * It DELETES the employee — `DELETE /merchant-employees/{id}` detaches them
 * from the merchant and cascades their branch assignments. It used to revoke
 * one branch instead, back when the service had no employee delete, which is
 * why the copy and the gating both changed with it:
 *
 * - **No branch is needed.** The delete addresses the employee, so the action
 *   is always available — the `canRemove` gate the callers used to consult is
 *   gone with the branch it was guarding.
 * - **The confirmation may say "delete".** The row really does leave the
 *   roster, and it does not come back on the refetch.
 *
 * Returns `true` when a delete actually landed, so a caller (the mobile sheet)
 * can close itself only on success.
 */
export function useRemoveUser() {
  const { t } = useTranslation('eats');
  const remove = useDeleteUser();

  async function removeUser(user: User): Promise<boolean> {
    let removed = false;
    const confirmed = await confirmDialog({
      title: t('users.deleteConfirm.title'),
      description: t('users.deleteConfirm.description', {
        name: user.fullName || t('users.card.unnamed'),
      }),
      confirmLabel: t('users.deleteConfirm.confirm'),
      cancelLabel: t('users.deleteConfirm.cancel'),
      destructive: true,
      onConfirm: async () => {
        try {
          await remove.mutateAsync(user.id);
          toast.success(t('users.deleteConfirm.success'));
          removed = true;
        } catch (error) {
          toast.error(getApiErrorMessage(error, t('users.form.errorTitle')));
          throw error;
        }
      },
    });
    return confirmed && removed;
  }

  return { removeUser, isPending: remove.isPending };
}
